import {Avatar, Box, Typography} from '@mui/material';
import {LoginRounded, LogoutRounded} from '@mui/icons-material';
import moment, {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import './attendance-registry-card.scss';
import {AttendanceManualBadge} from './attendance-manual-notice';
import AttendanceStatusChip from './attendance-status-chip';
import {
  AttendanceTrackerSession,
  AttendanceTrackerSessionPoint,
} from '../../backend/responses/attendance-tracker-session';
import {translations} from '../../i18n/translations';
import {
  formatSessionDuration,
  getSessionDurationSeconds,
  getSessionState,
} from '../../utils/functions/attendance-sessions';

type AttendanceRegistryCardProps = {
  session: AttendanceTrackerSession;
  /** Name of the professional of the session */
  professionalName?: string;
  /** Picture of the professional of the session */
  professionalPicture?: string;
  /** Current time according to the server, to know how long an open session is running */
  now: Moment;
  /** The card is clickable only when it can be selected (e.g. to open its details) */
  onSelect?: (session: AttendanceTrackerSession) => void;
};

type MovementColumnProps = {
  title: string;
  point?: AttendanceTrackerSessionPoint;
  /** What is shown when the movement was not registered yet */
  emptyMessage?: string;
  icon: React.ReactNode;
};

function MovementColumn({title, point, emptyMessage, icon}: MovementColumnProps) {
  return (
    <Box className="registry-movement">
      <Box className="registry-movement-title">
        {icon}
        <Typography>{title}</Typography>
      </Box>
      {point ? (
        <Box className="registry-movement-time">
          <Typography className="time">{moment(point.registeredAt).format('HH:mm')}</Typography>
          <Typography className="date">{moment(point.registeredAt).format('DD/MM')}</Typography>
        </Box>
      ) : (
        <Typography className="registry-movement-empty">{emptyMessage}</Typography>
      )}
      {/* A record registered by someone else, on behalf of the professional */}
      <AttendanceManualBadge point={point} />
    </Box>
  );
}

/**
 * A work session in the registry of the attendance tracker: the entry on the left and the exit on the right,
 * in columns with the same width.
 */
export default function AttendanceRegistryCard({
  session,
  professionalName,
  professionalPicture,
  now,
  onSelect,
}: AttendanceRegistryCardProps) {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker.session;

  const state = getSessionState(session, now);
  const isOpen = state === 'running' || state === 'overdue';
  const duration = formatSessionDuration(getSessionDurationSeconds(session, now));

  return (
    <Box
      className={`attendance-registry-card attendance-registry-card-${state}${onSelect ? ' attendance-registry-card-clickable' : ''}`}
      onClick={() => onSelect?.(session)}
    >
      <Box className="registry-card-header">
        <Box className="registry-card-professional">
          <Avatar src={professionalPicture} alt={professionalName} />
          <Typography className="professional-name">{professionalName}</Typography>
        </Box>
        <Box className="registry-card-status">
          {/* A voided session is not time worked */}
          {state !== 'voided' && (
            <Typography className="duration">
              {isOpen ? t(texts.elapsed, {duration}) : duration}
            </Typography>
          )}
          <AttendanceStatusChip state={state} />
        </Box>
      </Box>

      <Box className="registry-card-movements">
        <MovementColumn title={t(texts.entry)} point={session.entry} icon={<LoginRounded />} />
        <MovementColumn
          title={t(texts.exit)}
          point={session.exit}
          emptyMessage={t(translations.attendance_tracker_registry_screen.waiting_exit)}
          icon={<LogoutRounded />}
        />
      </Box>
    </Box>
  );
}
